import React, { useState } from 'react';
import { useLanguage } from "../context/LanguageContext";
import { useTheme } from "../context/ThemeContext";
import { ChevronDown } from 'lucide-react';
import { useSpring, animated } from '@react-spring/web';
import { useInView } from 'react-intersection-observer';

// Reusable link component
const ActionLink: React.FC<{ type: 'contact' | 'getStarted'; language: string }> = ({ type, language }) => {
  const handleClick = () => {
    // Show a confirmation dialog with privacy notice
    if (window.confirm(
      language === "en"
        ? "You will be redirected to our secure contact form hosted by Tally.so. Your information will be protected according to our privacy policy."
        : "Vous serez redirigé vers notre formulaire de contact sécurisé hébergé par Tally.so. Vos informations seront protégées conformément à notre politique de confidentialité."
    )) {
      const formUrl = language === "en" 
        ? 'https://tally.so/r/wvJ2jl'
        : 'https://tally.so/r/nPb4oQ';
      // Open in a more controlled way
      const win = window.open(formUrl, 'DynovantContactForm', 'noopener,noreferrer');
      if (win) win.opener = null;
    }
  };

  return (
    <button
      onClick={handleClick}
      className="text-green-500 hover:text-green-400 font-medium underline"
    >
      {type === 'contact'
        ? language === "en" ? "contact us" : "contactez-nous"
        : language === "en" ? "get started" : "commencer"}
    </button>
  );
};

interface FAQItem {
  id: number;
  questionEn: string;
  questionFr: string;
  answerEn: React.ReactNode;
  answerFr: React.ReactNode;
}

const FAQ: React.FC = () => {
  const { language } = useLanguage();
  const { theme } = useTheme();
  const [openItem, setOpenItem] = useState<string | null>(null);
  const [ref, inView] = useInView({ triggerOnce: true, threshold: 0.1 });
  const isDark = theme === "dark";

  const faqItems: FAQItem[] = [
    {
      id: 1,
      questionEn: "Who are we?",
      questionFr: "Qui sommes-nous ?",
      answerEn: (
        <div>
          We are a team of software engineers with experience in finance, automated manufacturing, and web development. At Dynovant Systems, we help businesses leverage AI, automation, and no-code tools to streamline operations, reduce costs, and drive growth.<br/><br/>
          Our expertise spans from process automation to AI-driven lead generation, chatbots, and data intelligence, ensuring businesses can work smarter, not harder.
        </div>
      ),
      answerFr: (
        <div>
          Nous sommes une équipe d'ingénieurs logiciels expérimentés en finance, fabrication automatisée et développement web. Chez Dynovant Systems, nous aidons les entreprises à tirer parti de l'IA, de l'automatisation et des outils no-code pour rationaliser les opérations, réduire les coûts et stimuler la croissance.<br/><br/>
          Notre expertise s'étend de l'automatisation des processus à la génération de leads basée sur l'IA, aux chatbots et à l'intelligence des données, permettant aux entreprises de travailler plus intelligemment.
        </div>
      )
    },
    {
      id: 2,
      questionEn: "What services does Dynovant Systems offer?",
      questionFr: "Quels services propose Dynovant Systems ?",
      answerEn: (
        <div>
          We specialize in:<br/>
          ✅ Workflow Automation & Integrations – Automate repetitive tasks and connect tools efficiently.<br/>
          ✅ AI Agents & Chatbots – Deploy intelligent assistants for customer support, research, and operations.<br/>
          ✅ AI-Powered Lead Generation – Automate prospecting and boost conversions using AI.<br/>
          ✅ Data Services – Web scraping, data analysis, forecasting, and visualization.<br/>
          ✅ Dashboards & Reporting – Create interactive dashboards for real-time business insights.<br/>
          ✅ Website & Content Systems – Build websites, automate content generation, and optimize digital workflows.<br/>
          ✅ Business Process Optimization – Audit and enhance internal processes to save time and money.
        </div>
      ),
      answerFr: (
        <div>
          Nous sommes spécialisés dans :<br/>
          ✅ Automatisation des flux de travail & Intégrations – Automatisez les tâches répétitives et connectez les outils efficacement.<br/>
          ✅ Agents IA & Chatbots – Déployez des assistants intelligents pour le support client, la recherche et les opérations.<br/>
          ✅ Génération de leads par IA – Automatisez la prospection et augmentez les conversions grâce à l'IA.<br/>
          ✅ Services de données – Scraping web, analyse de données, prévisions et visualisation.<br/>
          ✅ Tableaux de bord & Reporting – Créez des tableaux de bord interactifs pour des insights en temps réel.<br/>
          ✅ Sites Web & Systèmes de contenu – Créez des sites web, automatisez la génération de contenu et optimisez les flux de travail numériques.<br/>
          ✅ Optimisation des processus métier – Auditez et améliorez les processus internes pour économiser temps et argent.
        </div>
      )
    },
    {
      id: 3,
      questionEn: "How long does it take to deliver a project?",
      questionFr: "Combien de temps faut-il pour livrer un projet ?",
      answerEn: (
        <div>
          ⏳ Delivery times depend on complexity:<br/>
          🔹 Automations & Simple Integrations: 2-5 days<br/>
          🔹 AI Agents & Complex Workflows: 2-10 days<br/>
          🔹 Custom Software, Dashboards, & Advanced AI Models: 1-4 weeks<br/><br/>
          Need it faster? We offer priority delivery options. <ActionLink type="contact" language="en" /> to discuss your timeline!
        </div>
      ),
      answerFr: (
        <div>
          ⏳ Les délais de livraison dépendent de la complexité :<br/>
          🔹 Automatisations & Intégrations simples : 2-5 jours<br/>
          🔹 Agents IA & Flux de travail complexes : 2-10 jours<br/>
          🔹 Logiciels personnalisés, Tableaux de bord & Modèles IA avancés : 1-4 semaines<br/><br/>
          Besoin d'aller plus vite ? Nous proposons des options de livraison prioritaire. <ActionLink type="contact" language="fr" /> pour discuter de votre calendrier !
        </div>
      )
    },
    {
      id: 4,
      questionEn: "What industries do you work with?",
      questionFr: "Avec quels secteurs d'activité travaillez-vous ?",
      answerEn: (
        <div>
          We are effective in any industry with digital operations—which means virtually any business can benefit from our solutions. Whether you're looking to automate internal processes, generate AI-powered leads, or enhance data analytics, we have you covered.<br/><br/>
          📊 Industries we commonly serve include:<br/>
          🏢 Business Services & Consulting – Automate workflows and streamline operations.<br/>
          🛒 E-commerce & Retail – Optimize sales processes and customer interactions.<br/>
          💰 Finance & Investment – Leverage AI for data analysis and risk modeling.<br/>
          🏥 Healthcare & Medical – Improve administrative efficiency and patient interactions.<br/>
          📚 Education & E-learning – Automate course management and content generation.<br/>
          📦 Logistics & Supply Chain – Enhance tracking, reporting, and automation.
        </div>
      ),
      answerFr: (
        <div>
          Nous sommes efficaces dans tout secteur ayant des opérations numériques—ce qui signifie que pratiquement toute entreprise peut bénéficier de nos solutions. Que vous cherchiez à automatiser des processus internes, générer des leads par IA ou améliorer l'analyse de données, nous avons la solution.<br/><br/>
          📊 Les secteurs que nous servons couramment incluent :<br/>
          🏢 Services aux entreprises & Conseil – Automatisation des flux et rationalisation des opérations.<br/>
          🛒 E-commerce & Distribution – Optimisation des processus de vente et des interactions clients.<br/>
          💰 Finance & Investissement – Exploitation de l'IA pour l'analyse des données et la modélisation des risques.<br/>
          🏥 Santé & Médical – Amélioration de l'efficacité administrative et des interactions avec les patients.<br/>
          📚 Éducation & E-learning – Automatisation de la gestion des cours et génération de contenu.<br/>
          📦 Logistique & Supply Chain – Amélioration du suivi, du reporting et de l'automatisation.
        </div>
      )
    },
    {
      id: 5,
      questionEn: "What technologies do you use?",
      questionFr: "Quelles technologies utilisez-vous ?",
      answerEn: (
        <div>
          We focus on no-code and low-code tools for their flexibility, scalability, and ease of use. Our primary stack includes:<br/><br/>
          🔹 Workflow Automation:<br/>
          n8n (self-hosted & private automation platform)<br/>
          Make.com & Zapier (used selectively for specific workflows)<br/><br/>
          🔹 AI & Machine Learning:<br/>
          PyTorch / FastAI (Deep learning & AI models)<br/>
          Prophet, Scikit-Learn (Time-series forecasting & machine learning)<br/>
          LangFuse (LLM logging & monitoring for AI-driven workflows)<br/><br/>
          🔹 Data & Dashboards:<br/>
          Airtable, NocoDB (Database & workflow management)<br/>
          Grafana (Real-time dashboarding & analytics)<br/><br/>
          We strive to simplify solutions by using a single, powerful platform whenever possible, reducing complexity and maintenance.
        </div>
      ),
      answerFr: (
        <div>
          Nous privilégions les outils no-code et low-code pour leur flexibilité, leur évolutivité et leur facilité d'utilisation. Notre stack principal comprend :<br/><br/>
          🔹 Automatisation des flux de travail :<br/>
          n8n (plateforme d'automatisation auto-hébergée & privée)<br/>
          Make.com & Zapier (utilisés sélectivement pour des flux spécifiques)<br/><br/>
          🔹 IA & Machine Learning :<br/>
          PyTorch / FastAI (Deep learning & modèles IA)<br/>
          Prophet, Scikit-Learn (Prévisions temporelles & machine learning)<br/>
          LangFuse (Logging LLM & monitoring pour les flux basés sur l'IA)<br/><br/>
          🔹 Données & Tableaux de bord :<br/>
          Airtable, NocoDB (Gestion de base de données & flux de travail)<br/>
          Grafana (Tableaux de bord en temps réel & analytique)<br/><br/>
          Nous nous efforçons de simplifier les solutions en utilisant une seule plateforme puissante lorsque possible, réduisant la complexité et la maintenance.
        </div>
      )
    },
    {
      id: 6,
      questionEn: "How do you price your services?",
      questionFr: "Comment tarifez-vous vos services ?",
      answerEn: (
        <div>
          💡 We charge only a fraction of the value we bring.<br/><br/>
          Rather than just selling hours of work, our pricing is based on the impact and efficiency gains you achieve with our solutions. Whether it's saving hundreds of work hours through automation or generating new revenue streams with AI-driven lead generation, our goal is to provide the maximum ROI for your investment.<br/><br/>
          Pricing depends on the project scope and complexity—<ActionLink type="contact" language="en" /> for a custom quote!
        </div>
      ),
      answerFr: (
        <div>
          💡 Nous ne facturons qu'une fraction de la valeur que nous apportons.<br/><br/>
          Plutôt que de simplement vendre des heures de travail, nos tarifs sont basés sur l'impact et les gains d'efficacité que vous obtenez avec nos solutions. Qu'il s'agisse d'économiser des centaines d'heures de travail grâce à l'automatisation ou de générer de nouveaux revenus avec la génération de leads par IA, notre objectif est de fournir le ROI maximal pour votre investissement.<br/><br/>
          Les prix dépendent de l'étendue et de la complexité du projet—<ActionLink type="contact" language="fr" /> pour un devis personnalisé !
        </div>
      )
    },
    {
      id: 7,
      questionEn: "Can you integrate with my existing tools and systems?",
      questionFr: "Pouvez-vous vous intégrer à mes outils et systèmes existants ?",
      answerEn: (
        <div>
          Yes! We integrate with a wide range of CRMs, ERPs, APIs, databases, and third-party apps, including:<br/><br/>
          📊 HubSpot, Salesforce, Notion, Google Sheets, Zapier, Airtable, Slack, WhatsApp, Stripe, and more.<br/><br/>
          If your tool has an API, we can connect it!
        </div>
      ),
      answerFr: (
        <div>
          Oui ! Nous nous intégrons à une large gamme de CRM, ERP, API, bases de données et applications tierces, notamment :<br/><br/>
          📊 HubSpot, Salesforce, Notion, Google Sheets, Zapier, Airtable, Slack, WhatsApp, Stripe, et plus encore.<br/><br/>
          Si votre outil dispose d'une API, nous pouvons le connecter !
        </div>
      )
    },
    {
      id: 8,
      questionEn: "Can you help optimize our internal processes?",
      questionFr: "Pouvez-vous nous aider à optimiser nos processus internes ?",
      answerEn: (
        <div>
          Absolutely! We specialize in analyzing and automating business processes to:<br/><br/>
          🔍 Identify inefficiencies → ⚙️ Automate workflows → 💰 Reduce costs & boost efficiency<br/><br/>
          We work with businesses of all sizes to ensure that no digital task is wasted on manual effort when automation can do it faster and better.
        </div>
      ),
      answerFr: (
        <div>
          Absolument ! Nous sommes spécialisés dans l'analyse et l'automatisation des processus métier pour :<br/><br/>
          🔍 Identifier les inefficacités → ⚙️ Automatiser les flux de travail → 💰 Réduire les coûts & augmenter l'efficacité<br/><br/>
          Nous travaillons avec des entreprises de toutes tailles pour garantir qu'aucune tâche numérique n'est gaspillée en effort manuel lorsque l'automatisation peut la faire plus rapidement et mieux.
        </div>
      )
    },
    {
      id: 9,
      questionEn: "How do I get started?",
      questionFr: "Comment commencer ?",
      answerEn: (
        <div>
          📩 Step 1: <ActionLink type="getStarted" language="en" /> or <ActionLink type="contact" language="en" />.<br/>
          📝 Step 2: Share your project details or problem statement.<br/>
          🤝 Step 3: We analyze your needs and propose a tailored solution.<br/>
          🚀 Step 4: Once confirmed, we start building your automation or AI solution!
        </div>
      ),
      answerFr: (
        <div>
          📩 Étape 1: <ActionLink type="getStarted" language="fr" /> ou <ActionLink type="contact" language="fr" />.<br/>
          📝 Étape 2: Partagez les détails de votre projet ou la description du problème.<br/>
          🤝 Étape 3: Nous analysons vos besoins et proposons une solution sur mesure.<br/>
          🚀 Étape 4: Une fois confirmé, nous commençons à construire votre solution d'automatisation ou d'IA !
        </div>
      )
    },
    {
      id: 10,
      questionEn: "Do you offer ongoing support & maintenance?",
      questionFr: "Proposez-vous un support et une maintenance continus ?",
      answerEn: (
        <div>
          Yes! We provide:<br/>
          🔄 Ongoing support for automations, AI workflows, and integrations.<br/>
          ⚙️ Maintenance plans to ensure long-term reliability.<br/>
          📢 Updates & improvements as technology evolves.<br/><br/>
          We can also train your team to manage and extend the automations we implement.
        </div>
      ),
      answerFr: (
        <div>
          Oui ! Nous fournissons :<br/>
          🔄 Support continu pour les automatisations, flux de travail IA et intégrations.<br/>
          ⚙️ Plans de maintenance pour assurer la fiabilité à long terme.<br/>
          📢 Mises à jour & améliorations au fil de l'évolution technologique.<br/><br/>
          Nous pouvons également former votre équipe à gérer et étendre les automatisations que nous mettons en place.
        </div>
      )
    },
    {
      id: 11,
      questionEn: "Can I request a demo or consultation?",
      questionFr: "Puis-je demander une démo ou une consultation ?",
      answerEn: (
        <div>
          Of course! We offer free initial consultations to discuss your needs and explore potential solutions.<br/><br/>
          📆 <ActionLink type="getStarted" language="en" /> today!
        </div>
      ),
      answerFr: (
        <div>
          Bien sûr ! Nous proposons des consultations initiales gratuites pour discuter de vos besoins et explorer les solutions potentielles.<br/><br/>
          📆 <ActionLink type="getStarted" language="fr" /> aujourd'hui !
        </div>
      )
    }
  ];

  const handleItemClick = (id: number) => {
    const itemKey = `${Math.ceil(id / Math.ceil(faqItems.length / 2))}-${id}`;
    setOpenItem(openItem === itemKey ? null : itemKey);
  };

  const animation = useSpring({
    from: { 
      opacity: 0, 
      transform: 'translateY(50px)'
    },
    to: { 
      opacity: inView ? 1 : 0, 
      transform: inView ? 'translateY(0px)' : 'translateY(50px)'
    },
    config: { tension: 120, friction: 14 },
    delay: 200,
  });

  // Split items into two columns
  const leftItems = faqItems.slice(0, Math.ceil(faqItems.length / 2));
  const rightItems = faqItems.slice(Math.ceil(faqItems.length / 2));

  const renderFAQItem = (item: FAQItem, side: 'left' | 'right') => (
    <div
      key={`${side}-${item.id}`}
      className={`
        rounded-xl
        border-2
        overflow-hidden
        transition-all
        duration-300
        hover:-translate-y-1
        ${isDark 
          ? "bg-black/80 border-green-500/30 hover:border-green-400" 
          : "bg-white/80 border-green-900/30 hover:border-green-800"
        }
      `}
    >
      <button
        onClick={() => handleItemClick(item.id)}
        className="w-full text-left p-4 sm:p-6 flex items-start justify-between gap-4"
        aria-expanded={openItem === `${Math.ceil(item.id / Math.ceil(faqItems.length / 2))}-${item.id}`}
      >
        <h3 className={`text-base sm:text-lg md:text-xl font-semibold ${
          isDark ? "text-gray-200" : "text-gray-800"
        }`}>
          {language === "en" ? item.questionEn : item.questionFr}
        </h3>
        <ChevronDown
          className={`w-5 h-5 sm:w-6 sm:h-6 flex-shrink-0 transition-transform duration-300 ${
            openItem === `${Math.ceil(item.id / Math.ceil(faqItems.length / 2))}-${item.id}` ? "rotate-180" : ""
          } ${isDark ? "text-green-400" : "text-green-900"}`}
        />
      </button>
      <div
        className={`
          overflow-hidden
          transition-all
          duration-300
          ${openItem === `${Math.ceil(item.id / Math.ceil(faqItems.length / 2))}-${item.id}` ? "max-h-[1000px]" : "max-h-0"}
        `}
      >
        <div className={`px-4 sm:px-6 pb-4 sm:pb-6 text-sm sm:text-base md:text-lg ${
          isDark ? "text-gray-300" : "text-gray-700"
        }`}>
          {language === "en" ? item.answerEn : item.answerFr}
        </div>
      </div>
    </div>
  );

  return (
    <section 
      ref={ref} 
      id="faq" 
      className="relative py-24 sm:py-28 md:py-32 px-4 sm:px-6 lg:px-8 overflow-hidden"
    >
      <animated.div style={animation} className="max-w-[1600px] mx-auto">
        {/* Title */}
        <div className="text-center mb-12 sm:mb-16 md:mb-20">
          <h2 className={`
            text-3xl sm:text-4xl md:text-5xl lg:text-6xl 
            font-bold 
            ${isDark 
              ? "bg-gradient-to-r from-green-400 via-green-500 to-green-400" 
              : "bg-gradient-to-r from-green-900 via-green-800 to-green-900"
            }
            bg-clip-text 
            text-transparent
            mb-4 sm:mb-6
            animate-gradient-x
          `}>
            {language === "en" ? "Frequently Asked Questions" : "Questions Fréquentes"}
          </h2>
          <div className={`w-16 sm:w-20 md:w-24 h-1 mx-auto rounded-full mt-4 sm:mt-6 ${
            isDark 
              ? "bg-gradient-to-r from-green-500/50 to-green-300/50" 
              : "bg-gradient-to-r from-green-900/50 to-green-700/50"
          }`} />
        </div>

        {/* FAQ Grid */}
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-4 sm:gap-6 lg:gap-8">
          {/* Left Column */}
          <div className="space-y-4">
            {leftItems.map(item => renderFAQItem(item, 'left'))}
          </div>
          {/* Right Column */}
          <div className="space-y-4">
            {rightItems.map(item => renderFAQItem(item, 'right'))}
          </div>
        </div>
      </animated.div>

      {/* Background decorative elements */}
      <div className="absolute top-0 left-0 w-full h-full overflow-hidden pointer-events-none">
        <div className={`absolute top-1/4 right-1/4 w-48 md:w-96 h-48 md:h-96 rounded-full blur-3xl ${
          isDark ? "bg-green-500/5" : "bg-green-900/5"
        }`} />
        <div className={`absolute bottom-1/4 left-1/4 w-48 md:w-96 h-48 md:h-96 rounded-full blur-3xl ${
          isDark ? "bg-green-500/5" : "bg-green-900/5"
        }`} />
      </div>
    </section>
  );
};

export default FAQ;